const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const venue = /^bfv-[a-f0-9]{24}$/;
const target = /^backflow-[a-f0-9]{24}$/;
const revision = /^[a-f0-9]{64}$/;
const money = value => typeof value === 'string' && /^\d+\.\d{2}$/.test(value);
const count = value => Number.isSafeInteger(value) && value >= 0;
const privateKeys = new Set(['memberId', 'rules', 'rule', 'receipt', 'backflowReceipt', 'walletTransactionId', 'whiteListId', 'whitelistId', 'ipHash', 'deviceHash', 'vipAmounts', 'bonusOrderId', 'venueTransferOrderId', 'backflowVenue', 'venueQualification', 'providerBalanceBefore', 'productType', 'referenceNo', 'username', 'venueChoices', 'paidRequestIds']);
const publicOnly = value => !value || typeof value !== 'object' || Object.entries(value).every(([key, child]) => !privateKeys.has(key) && publicOnly(child));

// Validate the authoritative public projection before enabling a financial action.
export function validPageBackflowState(state, activityId) {
  return state && String(state.activityId) === String(activityId) && count(state.serverNow) && money(state.walletBalance) && publicOnly(state) &&
    typeof state.acknowledgedRequestId === 'string' && (!state.acknowledgedRequestId || uuid.test(state.acknowledgedRequestId)) &&
    Array.isArray(state.confirmedRequestIds) && state.confirmedRequestIds.every(id => uuid.test(id)) &&
    Array.isArray(state.offers) && new Set(state.offers.map(row => row.key)).size === state.offers.length && state.offers.every(row =>
      target.test(row.key) && revision.test(row.revision) && ['parentKey', 'title', 'startAt', 'endAt', 'reason'].every(key => typeof row[key] === 'string') &&
      [1, 2, 3, 4].includes(row.mode) && ['1', '2'].includes(row.wallet) && typeof row.canClaim === 'boolean' && typeof row.claimed === 'boolean' &&
      (row.venues === undefined || Array.isArray(row.venues) && new Set(row.venues.map(option => option.key)).size === row.venues.length && row.venues.every(option => venue.test(option.key) && typeof option.name === 'string' && option.name.trim().length > 0)) &&
      (!(row.wallet === '2' && [1, 3].includes(row.mode) && row.canClaim) || row.venues?.length > 0) &&
      money(row.amount) && money(row.paid) && typeof row.flow === 'string' && /^[1-9]\d{0,7}$/.test(row.flow) && count(row.recordId) &&
      (!row.claimed || row.recordId > 0 && Number(row.paid) > 0) && (!row.canClaim || Number(row.amount) > 0 && (!row.claimed || row.mode === 3)) &&
      (row.claimed || row.recordId === 0 && row.paid === '0.00'));
}

// Reads never submit a claim. Unknown outcomes retain the original UUID and rule revision through refresh or re-login.
export function createPageBackflowSession({ api, identity, token, storage, newRequestId, changed = () => {} }) {
  let activityId, actor, openedToken, alive = true, version = 0, readVersion = 0;
  let value = { state: null, loading: false, busy: false, error: '', pending: null };
  const publish = patch => { value = { ...value, ...patch }; changed(value); };
  const storageKey = () => `page-backflow:${actor}:${activityId}`;
  const current = sequence => alive && sequence === version && actor === identity() && openedToken === token();
  function readPending() {
    const saved = storage.get(storageKey());
    if (!saved) return null;
    if (saved.venueKey !== undefined && !venue.test(saved.venueKey) || !uuid.test(saved.requestId) || !target.test(saved.targetKey) || !revision.test(saved.revision) || Object.keys(saved).some(key => !['targetKey', 'revision', 'requestId', 'venueKey'].includes(key))) throw new Error('上次领取请求待核对，请保留原记录');
    return saved;
  }
  function accept(state, partial = false) {
    if (!validPageBackflowState(state, activityId)) throw new Error('回流奖励状态暂不可用，请重新查询');
    let pending = readPending();
    const offer = pending && state.offers.find(row => row.key === pending.targetKey);
    // A venue reservation acknowledged by the server is committed but remains unpaid until reconciliation.
    const venueReserved = [1, 2, 3].includes(offer?.mode) && offer.wallet === '2' && !offer.canClaim && state.acknowledgedRequestId === pending?.requestId;
    if (pending && (venueReserved || offer?.claimed && (state.confirmedRequestIds.includes(pending.requestId) || state.acknowledgedRequestId === pending.requestId || offer.mode !== 3))) {
      storage.remove(storageKey()); pending = null;
    }
    const offers = partial && value.state ? [...value.state.offers.filter(row => !state.offers.some(next => next.key === row.key)), ...state.offers] : state.offers;
    publish({ state: { ...state, offers }, pending, error: '' });
  }
  async function refresh() {
    if (!actor || !openedToken || !current(version) || value.busy) return;
    const sequence = version, read = ++readVersion;
    publish({ loading: true, error: '' });
    try { const state = await api.state(activityId); if (current(sequence) && read === readVersion) accept(state); }
    catch (error) { if (current(sequence) && read === readVersion) publish({ error: error.message || '回流奖励读取失败' }); }
    finally { if (current(sequence) && read === readVersion) publish({ loading: false }); }
  }
  async function open(id) {
    version++; readVersion++; alive = true; activityId = id; actor = identity(); openedToken = token();
    value = { state: null, loading: false, busy: false, error: '', pending: null }; publish({});
    if (!actor || !openedToken) return;
    try { publish({ pending: readPending() }); } catch (error) { publish({ error: error.message }); return; }
    await refresh();
  }
  async function send(data) {
    if (!current(version) || value.busy || value.loading || !value.state) return;
    const sequence = version;
    try {
      const saved = readPending();
      if (saved && JSON.stringify(saved) !== JSON.stringify(data)) throw new Error('请先核对上一次领取');
      storage.set(storageKey(), data); readVersion++; publish({ busy: true, pending: data, error: '' });
      const state = await api.claim(activityId, data);
      if (current(sequence)) accept(state, true);
    } catch (error) {
      if (current(sequence)) {
        if (error.errorCode === 'BACKFLOW_NOT_COMMITTED' && readPending()?.requestId === data.requestId) { storage.remove(storageKey()); publish({ pending: null }); }
        publish({ error: error.message || '领取结果待核对，请先查询原结果' });
      }
    } finally { if (current(sequence)) publish({ busy: false }); }
  }
  async function claim(key, venueKey = '') {
    if (!current(version) || value.pending || value.error || value.busy || value.loading) return;
    const offer = value.state?.offers.find(row => row.key === key);
    if (!offer?.canClaim) return;
    const needsVenue = offer.wallet === '2' && [1, 3].includes(offer.mode);
    if (needsVenue && !offer.venues?.some(option => option.key === venueKey)) return;
    const requestId = newRequestId();
    if (!uuid.test(requestId)) { publish({ error: '当前浏览器无法生成领取编号，请稍后重试' }); return; }
    await send({ targetKey: key, revision: offer.revision, requestId, ...(needsVenue ? { venueKey } : {}) });
  }
  return { open, refresh, claim, retry: () => value.pending && send(value.pending), dispose() { alive = false; version++; readVersion++; } };
}
